<template>
  <section class="mx-auto flex min-h-[70vh] max-w-4xl flex-col gap-8 px-6 py-16">
    <div class="flex flex-col gap-2">
      <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Notifications</p>
      <h1 class="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
        Activity
      </h1>
      <p class="text-base text-steel-200/90">
        Likes and comments on your posts.
      </p>
    </div>

    <div class="space-y-4">
      <div
        v-for="note in notifications"
        :key="note.id"
        class="rounded-2xl border border-white/10 bg-midnight-800/60 p-5"
      >
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
          <router-link
            :to="`/u/${note.actorHandle}`"
            class="flex items-center gap-3 hover:opacity-80 transition"
          >
            <div class="h-10 w-10 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
              <img
                v-if="note.actorAvatar"
                :src="note.actorAvatar"
                alt="Actor avatar"
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-xs font-semibold text-steel-200">
                {{ note.actorInitials }}
              </div>
            </div>
          </router-link>
          <div class="min-w-0 flex-1">
            <p class="text-sm text-steel-200/90">
              <router-link :to="`/u/${note.actorHandle}`" class="font-semibold text-white hover:text-neon-400">
                {{ note.actorName }}
              </router-link>
              <router-link :to="`/u/${note.actorHandle}`" class="text-steel-400 hover:text-neon-400">
                (@{{ note.actorHandle }})
              </router-link>
              <span> {{ note.message }}</span>
            </p>
            <p class="text-xs text-steel-400">{{ note.createdLabel }}</p>
          </div>
          <div class="flex flex-wrap items-center gap-4 sm:ml-auto">
            <router-link
              v-if="note.post_id"
              class="text-xs uppercase tracking-[0.3em] text-neon-400 hover:text-neon-500"
              :to="`/post/${note.post_id}`"
            >
              View
            </router-link>
            <button
              type="button"
              class="text-xs uppercase tracking-[0.3em] text-steel-400 hover:text-white"
              @click="deleteNotification(note.id)"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <p v-if="errorMessage" class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
      {{ errorMessage }}
    </p>
  </section>
</template>

<script>
import { supabase, getAvatarUrl } from '@/lib/supabase'

export default {
  name: 'NotificationsView',
  data() {
    return {
      notifications: [],
      errorMessage: '',
      userId: '',
    }
  },
  async mounted() {
    const { data } = await supabase.auth.getSession()
    this.userId = data?.session?.user?.id || ''
    await this.fetchNotifications()
  },
  methods: {
    async fetchNotifications() {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to view notifications.'
        return
      }

      const { data, error } = await supabase
        .from('notifications')
        .select(
          'id, type, created_at, post_id, comment_id, actor_id, actor:profiles!notifications_actor_id_fkey (handle, full_name, avatar_url, avatar_path)'
        )
        .eq('recipient_id', this.userId)
        .order('created_at', { ascending: false })

      if (error) {
        this.errorMessage = 'Unable to load notifications.'
        return
      }

      this.notifications = (data || []).map((note) => {
        const profile = note.actor || {}
        const actorAvatar = getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url })

        const messageMap = {
          post_like: 'liked your post.',
          post_comment: 'commented on your post.',
          comment_reply: 'replied to your comment.',
          comment_like: 'liked your comment.',
        }

        return {
          ...note,
          actorName: profile.full_name || 'DRVN Member',
          actorHandle: profile.handle || 'member',
          actorAvatar,
          actorInitials: (profile.full_name || 'D')
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((chunk) => chunk[0].toUpperCase())
            .join(''),
          message: messageMap[note.type] || 'interacted with you.',
          createdLabel: new Date(note.created_at).toLocaleString(),
        }
      })
    },
    async deleteNotification(noteId) {
      const { error } = await supabase.from('notifications').delete().eq('id', noteId)
      if (error) {
        this.errorMessage = error.message
        return
      }
      this.notifications = this.notifications.filter((note) => note.id !== noteId)
    },
  },
}
</script>
